import { lazy, Suspense } from 'react';
import { Outlet, RouteObject } from 'react-router';
import { Spin } from 'antd';
import { ProtectedRoutes } from '@/router/ProtectedRoutes.tsx';
import { Role } from '@/features/auth/auth.def.ts';

const InvestorDashboardPage = lazy(() => import('./investor-dashboard-page.tsx'));
const InvestorInteractionsPage = lazy(() => import('./investor-interactions-page.tsx'));
const InvestorReconciliationPage = lazy(() => import('./investor-reconciliation-page.tsx'));
const SnowflakeImportPage = lazy(() => import('./snowflake-import-page.tsx'));
const UploadInvestorWorkbookPage = lazy(() => import('./upload-investor-workbook-page.tsx'));
const UploadInvestmentRecordsPage = lazy(() => import('./upload-investment-records-page.tsx'));
const InvestorProfilePage = lazy(() => import('./investor-profile-page.tsx'));
const InvestorDashboardAnalyticsView = lazy(() =>
  import('./investor-dashboard-page.tsx').then(({ InvestorDashboardView }) => ({
    default: InvestorDashboardView,
  })),
);

/** The Investors tab body under the Analytics shell (`pages/analytics`). */
export const investorDashboardAnalyticsRoute: RouteObject = {
  path: 'investors',
  element: (
    <Suspense fallback={<Spin />}>
      <InvestorDashboardAnalyticsView />
    </Suspense>
  ),
};

export const investorManagementRoutes: RouteObject = {
  path: 'investor-management',
  element: <Outlet />,
  children: [
    {
      path: 'dashboard',
      element: (
        <Suspense fallback={<Spin />}>
          <InvestorDashboardPage />
        </Suspense>
      ),
    },
    {
      path: 'interactions',
      element: (
        <Suspense fallback={<Spin />}>
          <InvestorInteractionsPage />
        </Suspense>
      ),
    },
    {
      // UX gate only; SynchronizationController's [AuthorizeFunction] attributes are the security boundary.
      element: (
        <ProtectedRoutes
          allowRoles={[Role.Developer, Role.InvestorRelations]}
          requiredPermissions={[
            { moduleName: 'investor-management', functionName: 'review-reconciliation-plan' },
          ]}
        />
      ),
      children: [
        {
          path: 'reconciliation',
          element: (
            <Suspense fallback={<Spin />}>
              <InvestorReconciliationPage />
            </Suspense>
          ),
        },
      ],
    },
    {
      element: <ProtectedRoutes allowRoles={[Role.Developer]} />,
      children: [
        {
          path: 'snowflake-import',
          element: (
            <Suspense fallback={<Spin />}>
              <SnowflakeImportPage />
            </Suspense>
          ),
        },
      ],
    },
    {
      path: 'upload-investor-workbook',
      element: (
        <Suspense fallback={<Spin />}>
          <UploadInvestorWorkbookPage />
        </Suspense>
      ),
    },
    {
      path: 'upload-investment-records',
      element: (
        <Suspense fallback={<Spin />}>
          <UploadInvestmentRecordsPage />
        </Suspense>
      ),
    },
    {
      path: 'investor/:investorId',
      element: (
        <Suspense fallback={<Spin />}>
          <InvestorProfilePage />
        </Suspense>
      ),
    },
  ],
};
